import { useQuery } from '@apollo/client/react'

import { useSplits } from '@buffer-mono/features'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { logError } from '~publish/legacy/utils/logError'

import { EXCLUDED_ROLLOUT_STATUSES } from '../constants'

export const GetBetaFeatures = graphql(/* GraphQL */ `
  query getBetaFeatures {
    betaFeatures {
      name
      description
      feedbackUrl
      platforms
      splitName
      rolloutStatus {
        id
        name
      }
    }
  }
`)

export type BetaFeature = NonNullable<
  ResultOf<typeof GetBetaFeatures>['betaFeatures']
>[number]

type GetBetaFeatures = {
  betaFeatures: BetaFeature[] | null
  splits: Record<string, SplitIO.TreatmentWithConfig>
  isLoading: boolean
  hasError: boolean
}

/**
 * Custom hook to query the beta features
 * @returns {GetBetaFeatures} The accounts beta features
 */
export const useBetaFeatures = (): GetBetaFeatures => {
  const { data, loading, error } = useQuery(GetBetaFeatures)

  const names = data?.betaFeatures?.map((feature) => feature.splitName) ?? []
  const { splits } = useSplits(names)

  if (!data?.betaFeatures && !loading && !error) {
    return {
      betaFeatures: [],
      splits: {},
      isLoading: false,
      hasError: true,
    }
  }

  // Handle GraphQL errors if any
  if (error) {
    const unexpectedError = new Error(
      error?.message || 'Unexpected API response',
    )
    logError(unexpectedError, { metaData: { data, error } })
    return {
      betaFeatures: [],
      splits: {},
      isLoading: false,
      hasError: true,
    }
  }

  // only return web features, so filter any that are mobile, ios, or android
  const webFeatures = data?.betaFeatures?.filter(
    (feature: BetaFeature) =>
      !feature.platforms.includes('mobile') &&
      !feature.platforms.includes('ios') &&
      !feature.platforms.includes('android'),
  )

  // Filter out features with excluded rollout statuses
  const featuresWithoutExcludedStatuses =
    webFeatures?.filter((feature: BetaFeature) => {
      if (
        feature.rolloutStatus &&
        EXCLUDED_ROLLOUT_STATUSES.includes(
          feature.rolloutStatus.name.toLowerCase(),
        )
      ) {
        return false
      }
      return true
    }) ?? []

  // Filter out features with 'control' or 'hidden' treatment
  const visibleFeatures =
    featuresWithoutExcludedStatuses?.filter((feature: BetaFeature) => {
      const treatment = splits[feature.splitName]?.treatment
      return treatment !== 'control' && treatment !== 'hidden'
    }) ?? []

  return {
    betaFeatures: visibleFeatures,
    splits,
    isLoading: loading,
    hasError: !!error,
  }
}
